/**
 * SEO Utility Functions
 * Auto-generates SEO-friendly content for listings
 */

import { CATEGORY_LABELS } from './api';

const SEO_BASE_URL = `https://${import.meta.env.VITE_MEDIA_DOMAIN || 'www.patakare.com'}`;

// Generate URL-safe slug from text
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces/underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

// Generate SEO-friendly URL for a listing
export const generateListingUrl = (
  id: number,
  name: string,
  categoryType: number,
  city?: string,
  state?: string
): string => {
  const categorySlug = getCategorySlug(categoryType);
  const nameSlug = generateSlug(name);
  const locationSlug = city ? generateSlug(city) : state ? generateSlug(state) : '';
  
  // Format: /category/name-location-id
  // e.g., /hospital/apollo-hospital-mumbai-12345
  // e.g., /doctor/dr-rajesh-sharma-delhi-67890
  const parts = [nameSlug];
  if (locationSlug) parts.push(locationSlug);
  parts.push(String(id));
  
  return `/${categorySlug}/${parts.join('-')}`;
};

// Get category slug for URL
export const getCategorySlug = (categoryType: number): string => {
  const slugMap: Record<number, string> = {
    1: 'hospital',
    2: 'doctor',
    3: 'pharmacy',
    4: 'homecare',
    5: 'ambulance',
    6: 'diagnostic-center',
    7: 'wholesaler',
  };
  return slugMap[categoryType] || 'listing';
};

// Parse ID from SEO URL slug
export const parseIdFromSlug = (slug: string): number | null => {
  if (!slug) return null;
  
  // The ID is the last number in the slug (after the last dash)
  // Format: name-location-ID or name-ID
  const match = slug.match(/-(\d+)$/);
  if (match) return parseInt(match[1]);
  
  // Fallback: If the slug is just a number
  const numericSlug = parseInt(slug);
  if (!isNaN(numericSlug)) return numericSlug;
  
  // Try to find any number in the slug
  const anyNumber = slug.match(/(\d+)/);
  if (anyNumber) return parseInt(anyNumber[1]);
  
  return null;
};

// Admin-configurable copy from the "Global Settings" tab (SEOManagePage) — real
// per-category copy above is deliberately kept as the source of truth for every
// category type that already has one; these templates only fill in for a
// category type added before hardcoded copy exists for it (e.g. a new listing
// type), and always add to (never replace) the keyword list.
export interface GlobalSeoTemplate {
  titleTemplate?: string;
  descriptionTemplate?: string;
  defaultKeywords?: string;
}

export const applySeoTemplate = (template: string, vars: Record<string, string>): string => {
  let result = template;
  Object.entries(vars).forEach(([key, value]) => {
    result = result.replace(new RegExp(`{{${key}}}`, 'g'), value);
  });
  return result;
};

// Generate meta title (max 60 chars for Google)
export const generateMetaTitle = (
  name: string,
  categoryType: number,
  city?: string,
  specialization?: string,
  globalTemplate?: GlobalSeoTemplate
): string => {
  const knownCategory = CATEGORY_LABELS[categoryType];
  const category = knownCategory || 'Healthcare';

  // No hardcoded copy for this category type yet — use the admin's template
  // instead of the bare "{name} in {city} | Patakare" fallback below.
  if (!knownCategory && globalTemplate?.titleTemplate) {
    const templated = applySeoTemplate(globalTemplate.titleTemplate, {
      name, category, city: city || '', state: '',
    });
    return templated.length > 60 ? templated.substring(0, 57) + '...' : templated;
  }

  let title = '';

  if (categoryType === 2) {
    // Doctor
    title = specialization
      ? `${name} - ${specialization}`
      : `${name} - ${category}`;
  } else {
    title = name;
  }

  if (city) {
    title += ` in ${city}`;
  }

  title += ' | Patakare';

  // Truncate to 60 chars
  if (title.length > 60) {
    title = title.substring(0, 57) + '...';
  }

  return title;
};

// Generate meta description (max 160 chars for Google)
export const generateMetaDescription = (
  name: string,
  categoryType: number,
  city?: string,
  state?: string,
  specialization?: string,
  description?: string,
  globalTemplate?: GlobalSeoTemplate
): string => {
  const category = CATEGORY_LABELS[categoryType] || 'Healthcare';
  const location = city && state ? `${city}, ${state}` : city || state || 'India';
  
  // If custom description exists, use it (truncated)
  if (description && description.length > 50) {
    const cleaned = description.replace(/<[^>]*>/g, '').trim();
    return cleaned.length > 160 ? cleaned.substring(0, 157) + '...' : cleaned;
  }
  
  // Auto-generate description
  let desc = '';
  
  if (categoryType === 2) {
    // Doctor
    desc = `${name}${specialization ? `, ${specialization} specialist` : ''} in ${location}. Book appointment, view consultation hours, fees & reviews on Patakare.`;
  } else if (categoryType === 1) {
    // Hospital
    desc = `${name} - Leading hospital in ${location}. View services, departments, doctors, contact details & patient reviews on Patakare.`;
  } else if (categoryType === 3) {
    // Medical Store
    desc = `${name} - Trusted pharmacy in ${location}. Order medicines, healthcare products with home delivery. Check availability on Patakare.`;
  } else if (categoryType === 6) {
    // Diagnostic Center
    desc = `${name} - Diagnostic & pathology lab in ${location}. Book tests, view prices, home sample collection. Contact via Patakare.`;
  } else if (categoryType === 5 || categoryType === 10) {
    // Ambulance (legacy type 5 + current type 10)
    desc = `${name} - 24/7 ambulance service in ${location}. Emergency & non-emergency patient transport. Quick booking via Patakare.`;
  } else if (categoryType === 7) {
    // Wholesaler
    desc = `${name} - Trusted pharmaceutical wholesaler & distributor in ${location}. Bulk medicine supply, brands dealt & contact details on Patakare.`;
  } else if (categoryType === 4) {
    // HomeCare / Nurse
    desc = `${name} - Home healthcare services in ${location}. Book nurses, caregivers & attendants for at-home patient care on Patakare.`;
  } else if (categoryType === 8) {
    // Blood Bank
    desc = `${name} - Blood bank in ${location}. Check blood group availability, donate blood & emergency contact details on Patakare.`;
  } else if (categoryType === 9) {
    // Health Camp
    desc = `${name} - Health camp & wellness event in ${location}. View schedule, registration details & organizer contact on Patakare.`;
  } else if (globalTemplate?.descriptionTemplate) {
    // No hardcoded copy for this category type yet — use the admin's template.
    desc = applySeoTemplate(globalTemplate.descriptionTemplate, {
      name, category, city: city || '', state: state || '',
    });
  } else {
    // Generic
    desc = `${name} - ${category} services in ${location}. View contact details, timings & reviews on Patakare.`;
  }

  return desc.length > 160 ? desc.substring(0, 157) + '...' : desc;
};

// Generate SEO keywords
export const generateMetaKeywords = (
  name: string,
  categoryType: number,
  city?: string,
  state?: string,
  specialization?: string,
  services?: string[],
  globalTemplate?: GlobalSeoTemplate
): string => {
  const category = CATEGORY_LABELS[categoryType] || 'Healthcare';
  const keywords: string[] = [];
  
  // Name keywords
  keywords.push(name.toLowerCase());
  
  // Category keywords
  keywords.push(category.toLowerCase());
  keywords.push(`${category.toLowerCase()} near me`);
  
  // Location keywords
  if (city) {
    keywords.push(city.toLowerCase());
    keywords.push(`${category.toLowerCase()} in ${city.toLowerCase()}`);
    keywords.push(`best ${category.toLowerCase()} in ${city.toLowerCase()}`);
  }
  if (state) {
    keywords.push(state.toLowerCase());
  }
  
  // Specialization keywords (for doctors)
  if (specialization) {
    keywords.push(specialization.toLowerCase());
    keywords.push(`${specialization.toLowerCase()} doctor`);
    if (city) {
      keywords.push(`${specialization.toLowerCase()} in ${city.toLowerCase()}`);
    }
  }
  
  // Service keywords
  if (services && services.length > 0) {
    services.slice(0, 5).forEach(service => {
      keywords.push(service.toLowerCase());
    });
  }
  
  // Admin-configured base keywords (Global Settings tab) — additive, not a
  // replacement, so a listing keeps its specific keywords either way.
  if (globalTemplate?.defaultKeywords) {
    const extra = applySeoTemplate(globalTemplate.defaultKeywords, {
      name, category: category.toLowerCase(), city: (city || '').toLowerCase(), state: (state || '').toLowerCase(),
    }).split(',').map(k => k.trim().toLowerCase()).filter(Boolean);
    keywords.push(...extra);
  } else {
    keywords.push('patakare');
    keywords.push('healthcare');
    keywords.push('medical');
  }

  // Remove duplicates and join
  return [...new Set(keywords)].join(', ');
};

// Generate JSON-LD structured data for a listing
export const generateStructuredData = (listing: {
  id: number;
  name: string;
  categoryType: number;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  phone?: string;
  email?: string;
  website?: string;
  image?: string;
  rating?: number;
  reviewCount?: number;
  specialization?: string;
  qualification?: string;
  timing?: Record<string, { open: string; close: string; isOpen: boolean }>;
}, disabledSchemaTypes?: number[]): object | null => {
  // Admin turned this category's schema off (Schema Engine tab, SEOManagePage) —
  // real gate now instead of a localStorage toggle nothing checked.
  if (disabledSchemaTypes?.includes(listing.categoryType)) {
    return null;
  }

  const baseUrl = SEO_BASE_URL;
  const listingUrl = `${baseUrl}${generateListingUrl(
    listing.id,
    listing.name,
    listing.categoryType,
    listing.city,
    listing.state
  )}`;
  
  const fullAddress = [listing.address, listing.city, listing.state, 'India']
    .filter(Boolean)
    .join(', ');
  
  // Base schema
  const baseSchema = {
    '@context': 'https://schema.org',
    '@id': listingUrl,
    name: listing.name,
    // Always through generateMetaDescription() (not listing.description
    // directly) — it strips HTML tags from the provider's rich-text
    // description before use; the direct-OR version used to embed raw
    // "<h1><strong>..." markup straight into the JSON-LD, which likely
    // contributed to Search Console's invalid-structured-data findings.
    description: generateMetaDescription(
      listing.name,
      listing.categoryType,
      listing.city,
      listing.state,
      listing.specialization,
      listing.description
    ),
    url: listingUrl,
    image: listing.image || `${baseUrl}/favicon.jpg`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: listing.address || '',
      addressLocality: listing.city || '',
      addressRegion: listing.state || '',
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      addressCountry: 'IN',
    },
  };
  
  // Add contact info
  const contactInfo: Record<string, unknown> = {};
  if (listing.phone) {
    contactInfo.telephone = listing.phone;
  }
  if (listing.email) {
    contactInfo.email = listing.email;
  }
  if (listing.website) {
    contactInfo.sameAs = listing.website;
  }
  
  // Add rating if available
  const ratingInfo: Record<string, unknown> = {};
  if (listing.rating && listing.reviewCount) {
    ratingInfo.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: listing.rating,
      reviewCount: listing.reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }
  
  // Generate opening hours
  const openingHours: string[] = [];
  if (listing.timing) {
    const dayMap: Record<string, string> = {
      monday: 'Mo',
      tuesday: 'Tu',
      wednesday: 'We',
      thursday: 'Th',
      friday: 'Fr',
      saturday: 'Sa',
      sunday: 'Su',
    };
    
    Object.entries(listing.timing).forEach(([day, hours]) => {
      if (hours.isOpen && dayMap[day]) {
        openingHours.push(`${dayMap[day]} ${hours.open}-${hours.close}`);
      }
    });
  }
  
  // Category-specific schemas
  switch (listing.categoryType) {
    case 1: // Hospital
      return {
        ...baseSchema,
        '@type': 'Hospital',
        medicalSpecialty: listing.specialization || 'MultiSpecialty',
        ...contactInfo,
        ...ratingInfo,
        openingHoursSpecification: openingHours.length > 0 ? openingHours : undefined,
      };
      
    case 2: // Doctor
      return {
        ...baseSchema,
        '@type': 'Physician',
        medicalSpecialty: listing.specialization || 'General Practice',
        ...(listing.qualification && { 
          hasCredential: {
            '@type': 'EducationalOccupationalCredential',
            credentialCategory: 'degree',
            name: listing.qualification,
          }
        }),
        ...contactInfo,
        ...ratingInfo,
      };
      
    case 3: // Medical Store
      return {
        ...baseSchema,
        '@type': 'Pharmacy',
        ...contactInfo,
        ...ratingInfo,
        openingHoursSpecification: openingHours.length > 0 ? openingHours : undefined,
      };
      
    case 6: // Diagnostic Center
      return {
        ...baseSchema,
        '@type': 'DiagnosticLab',
        ...contactInfo,
        ...ratingInfo,
        openingHoursSpecification: openingHours.length > 0 ? openingHours : undefined,
      };
      
    case 10: // Ambulance (was case 5 — that's a dead/unused type_code; real ambulance
             // listings are type_code=10 everywhere else in this codebase, so this
             // branch never actually matched and every ambulance listing silently
             // fell through to the generic LocalBusiness schema below)
      return {
        ...baseSchema,
        '@type': 'EmergencyService',
        serviceType: 'Ambulance',
        ...contactInfo,
        availableChannel: {
          '@type': 'ServiceChannel',
          serviceType: '24/7 Emergency',
        },
      };

    case 4: // HomeCare+
      return {
        ...baseSchema,
        '@type': 'MedicalBusiness',
        medicalSpecialty: 'HomeHealthCare',
        ...contactInfo,
        ...ratingInfo,
      };

    case 8: // Blood Bank
      return {
        ...baseSchema,
        '@type': 'MedicalOrganization',
        medicalSpecialty: 'BloodBank',
        ...contactInfo,
        ...ratingInfo,
      };

    case 9: // Health Camp — a scheduled event, not a permanent business
      return {
        '@context': 'https://schema.org',
        '@type': 'MedicalEvent',
        '@id': baseSchema['@id'],
        name: listing.name,
        description: baseSchema.description,
        url: baseSchema.url,
        image: baseSchema.image,
        location: {
          '@type': 'Place',
          address: baseSchema.address,
        },
        ...contactInfo,
      };

    default:
      return {
        ...baseSchema,
        '@type': 'LocalBusiness',
        ...contactInfo,
        ...ratingInfo,
      };
  }
};

// Generate canonical URL
export const generateCanonicalUrl = (
  id: number,
  name: string,
  categoryType: number,
  city?: string,
  state?: string
): string => {
  const baseUrl = SEO_BASE_URL;
  return `${baseUrl}${generateListingUrl(id, name, categoryType, city, state)}`;
};

// Generate breadcrumb structured data
export const generateBreadcrumbData = (
  listing: {
    id: number;
    name: string;
    categoryType: number;
    city?: string;
    state?: string;
  }
): object => {
  const baseUrl = SEO_BASE_URL;
  const category = CATEGORY_LABELS[listing.categoryType] || 'Healthcare';

  // Every item's URL must actually resolve, or Google flags the whole BreadcrumbList
  // invalid — confirmed live in Search Console (1,240 invalid / 0 valid). Two bugs:
  // (1) the State/City crumbs linked to `/listings?...&state=X&city=Y`, but the real
  // listings page filters by a numeric city id, not a name string — neither param
  // it built was even read by that page. (2) the final "current page" crumb was built
  // from just the name slug, missing the location+id segment every real listing URL
  // requires (see generateListingUrl below) — a bare name slug 404s. Dropped the
  // State/City levels entirely (there's no numeric cityId available here to link them
  // correctly, and no visible on-page breadcrumb UI to match anyway) and reused
  // generateListingUrl() for the last item instead of re-deriving the URL a second,
  // divergent way.
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: baseUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: category,
      item: `${baseUrl}/listings?type=${listing.categoryType}`,
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: listing.name,
      item: `${baseUrl}${generateListingUrl(listing.id, listing.name, listing.categoryType, listing.city, listing.state)}`,
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
};

// Default page meta tags
export const defaultMeta = {
  title: 'Patakare - Healthcare Directory | Find Hospitals, Doctors & Medical Services',
  description: 'Patakare.com - Your trusted healthcare partner. Find hospitals, doctors, medical stores, diagnostic centers, ambulance services near you.',
  keywords: 'patakare, healthcare, hospitals, doctors, medical stores, pharmacies, diagnostic centers, ambulance, india, healthcare directory',
  ogImage: `${SEO_BASE_URL}/patakare-brand-logo-v2.png`,
  twitterHandle: '@Patakare',
};
